import { ToastType } from '@components/toast';
import { DocumentSearchRequestQuery, DocumentSearchResponse } from '@lio/api-types';
import { DocumentStatus } from '@models/document';
import { documentQueryKey } from '@services/document/keys';
import { useQuery, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { differenceInMilliseconds } from 'date-fns';
import { useSnackbar } from 'notistack';

const getSearchDocumentsQuery = (params: DocumentSearchRequestQuery) => {
  const {
    quoteId,
    pageLimit,
    pageOffset,
    documentGenerationId,
    documentGenerationStatus,
    documentTypeId,
    fileId,
    packageId,
    policyId,
    createdOnOrAfterDate,
    sort,
  } = params;
  const query = new URLSearchParams();
  if (quoteId) {
    query.append('quoteId', quoteId);
  }
  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }
  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }
  if (documentGenerationId) {
    query.append('documentGenerationId', documentGenerationId);
  }
  if (documentGenerationStatus) {
    query.append('documentGenerationStatus', documentGenerationStatus);
  }
  if (documentTypeId) {
    if (Array.isArray(documentTypeId)) {
      documentTypeId.forEach((id) => {
        if (id) query.append('documentTypeId', id);
      });
    } else {
      query.append('documentTypeId', documentTypeId);
    }
  }
  if (fileId) {
    query.append('fileId', fileId);
  }
  if (packageId) {
    query.append('packageId', packageId);
  }
  if (policyId) {
    query.append('policyId', policyId);
  }
  if (createdOnOrAfterDate) {
    const dateString = new Date(createdOnOrAfterDate).toJSON();
    query.append('createdOnOrAfterDate', dateString);
  }

  if (sort) {
    query.append('sort', sort);
  } else {
    query.append('sort', '-createdAt,-updatedAt');
  }

  return query.toString();
};

export const useSearchDocuments = (params: DocumentSearchRequestQuery, enabled: boolean = true) => {
  const client = useClient();
  const query = `${getSearchDocumentsQuery(params)}`;

  const { enqueueSnackbar } = useSnackbar();
  const result = useQuery<DocumentSearchResponse, Error>({
    queryKey: documentQueryKey.searchWithParams(params),
    queryFn: () => client.get(`document?${query}`).json<DocumentSearchResponse>(),
    gcTime: 15000,
    refetchOnWindowFocus: false,
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const { data } = query.state;
      const newDocument = data?.items.find((d) => d.documentGeneration?.id === params.documentGenerationId);
      if (
        newDocument?.documentGeneration?.status === DocumentStatus.Pending ||
        newDocument?.documentGeneration?.status === DocumentStatus.InProgress
      ) {
        const queryStartedAt = new Date(newDocument.createdAt);
        const now = new Date();
        const msSinceStarted = differenceInMilliseconds(now, queryStartedAt);
        if (msSinceStarted < 3000) {
          return 1000;
        }
        if (msSinceStarted < 10000) {
          return 2000;
        }
        if (msSinceStarted < 90000) {
          // give up polling after 90 seconds
          // TODO: provide useful messaging
          return 5000;
        }
      } else if (params.documentGenerationId && !newDocument) {
        // if we have the document generation id this should exist and we should have found it
        // sometimes new document is not yet returned and causing download buttons to do funny things (spin forever)
        return 3000;
      }

      return false;
    },
  });
  if (result.isError) {
    enqueueSnackbar('No document was found', {
      variant: ToastType.Error,
      persist: true,
    });
  }

  return { ...result, documents: result.data };
};

export const useSearchDocumentsInfinite = (params: DocumentSearchRequestQuery, enabled: boolean = true) => {
  const client = useClient();
  const query = `${getSearchDocumentsQuery(params)}`;

  const { enqueueSnackbar } = useSnackbar();
  const result = useInfiniteQuery<DocumentSearchResponse, Error>({
    queryKey: ['infinite', documentQueryKey.searchWithParams(params)],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await client.get(`document/?${query}&pageOffset=${pageParam}`).json<DocumentSearchResponse>();
      return response;
    },
    enabled,
    initialPageParam: 0,
    placeholderData: (previousData) => previousData,
    staleTime: 15000,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.pagination) return undefined;
      if (!lastPage.pagination.pageLimit) return undefined;
      if (!lastPage.pagination.total) return undefined;
      const { total, pageLimit } = lastPage.pagination;
      const fetchedPageCount = allPages.length;
      const nextPageOffsetValue = fetchedPageCount * pageLimit;
      if (nextPageOffsetValue > total) return undefined;
      return nextPageOffsetValue;
    },
  });
  if (result.isError) {
    enqueueSnackbar('No document was found', {
      variant: ToastType.Error,
      persist: true,
    });
  }

  return { ...result, searchDocuments: result.data };
};
